import { OpenFeature } from '@openfeature/react-sdk'
import * as Sentry from '@sentry/react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { jwtDecode } from 'jwt-decode'
import { useEffect } from 'react'

import { UserInfoResponse } from '~/clients/derived-api-types'
import { isDipsUrlConfigured } from '~/clients/dips-client'
import env from '~/env'
import { useAnalyticsToastDispatcher } from '~/hooks/useAnalyticsToastDIspatcher'
import { useFlag } from '~/hooks/useFlag'
import { VersionCheckProvider } from '~/hooks/useVersionCheck'
import { WebSocketProvider } from '~/hooks/useWebSocketUpdates/useWebSocketConnection'
import { clearPartialStorage, useStore } from '~/store/store'
import { analytics, anonymizedUser } from '~/utils/analytics'
import { isDeepinsightUser } from '~/utils/isDeepinsightUser'
import { expireSession } from '~/utils/session'
import { cancelRefreshingAccessToken, scheduleRefreshingAccessToken } from '~/utils/token'

import AuthenticatedAppContents from './AuthenticatedAppContents'
import AuthPage from './pages/Auth/Auth.page'
import PKCEAuthenticationFlow from './PKCEAuthenticationFlow'
import { useFetchUserInfo } from './useFetchUserInfo'
import { useLoadingScreen } from './useLoadingScreen'

function useSetAuthenticationData(data: UserInfoResponse | undefined): void {
    const { setAuth } = useStore(state => state.user.actions)
    useEffect(() => {
        if (data === undefined) return

        setAuth({
            display_name: data.userinfo.display_name,
            username: data.userinfo.username ?? null,
            read_only: data.userinfo.read_only,
            accessToken: data.access_token,
            issuer: data.issuer,
            planning_domain_uuids: data.userinfo.planning_domain_uuids,
        })
    }, [data, setAuth])
}

function useScheduleRefreshingAccessToken(data: UserInfoResponse | undefined) {
    const refreshTokenFeatureFlag = env.VITE_FF_REFRESH_TOKEN
    const { setAuth } = useStore(state => state.user.actions)

    useEffect(() => {
        if (!refreshTokenFeatureFlag || data === undefined) {
            return
        }

        scheduleRefreshingAccessToken({
            currentAccessToken: data,
            callback: newAccessToken => setAuth({ accessToken: newAccessToken.access_token }),
            onSessionExpired: expireSession,
        })
        return cancelRefreshingAccessToken
    }, [refreshTokenFeatureFlag, data, setAuth])
}

if (!isDipsUrlConfigured()) {
    console.warn('DIPS URL is not configured for this environment.')
}

const App = () => {
    const { data, isLoading } = useFetchUserInfo()
    const dipsApiAccessToken = useStore(state => state.user.dipsApiAccessToken)
    const { show, hide } = useLoadingScreen()
    Sentry.setTag('user.is_deepinsight', isDeepinsightUser(data?.issuer))
    const isDevToolsEnabled = useFlag('react_query__dev_tools')

    useAnalyticsToastDispatcher()
    useSetAuthenticationData(data)
    useScheduleRefreshingAccessToken(data)

    const needsAuth = !dipsApiAccessToken && (!isDeepinsightUser(data?.issuer, { includeMock: false }) || env.VITE_PKCE_FOR_DEVS)

    useEffect(() => {
        if (isLoading) return

        if (data?.userinfo) {
            Sentry.setUser({ username: data.userinfo.username ?? undefined })
        } else {
            Sentry.setUser(null)
        }

        if (data?.userinfo.username) {
            void OpenFeature.setContext({ targetingKey: data.userinfo.username })
        } else {
            void OpenFeature.clearContext() // fallback to anonymous mode
        }
    }, [isLoading, data?.userinfo])

    useEffect(() => {
        async function identifyUser() {
            if (dipsApiAccessToken) {
                const idToken = jwtDecode(dipsApiAccessToken)
                const user = await analytics?.user()
                const anonymousUserId = await anonymizedUser(idToken.sub ?? user?.anonymousId() ?? 'anonymous')
                void analytics?.identify(anonymousUserId, { tenant: env.VITE_SEGMENT_TENANT_NAME, environment: env.VITE_SENTRY_ENVIRONMENT })
            }
        }
        void identifyUser()
    }, [dipsApiAccessToken])

    if (isLoading) {
        show({ progressFraction: 0.5 })
        return null
    }

    if (data === undefined) {
        clearPartialStorage()
        hide()
        return <AuthPage />
    }

    if (needsAuth) {
        if (env.VITE_PKCE_AUTHORIZE_URL) {
            return <PKCEAuthenticationFlow />
        } else {
            show({ progressFraction: 0.75 })
            return null // prevents race condition where state.app.dipsApiAccessToken is still null (in non-PKCE flow). If we don't wait, a component below can call DIPS API → responds with 401 → we get logged out
        }
    }

    hide()

    return (
        <>
            <VersionCheckProvider>
                <WebSocketProvider>
                    <div data-test="authenticated" hidden />
                    <AuthenticatedAppContents />
                </WebSocketProvider>
            </VersionCheckProvider>
            <ReactQueryDevtools initialIsOpen={isDevToolsEnabled} />
        </>
    )
}

export default App
